import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authenticationService } from '../../services';

import { makeGet } from '../../api/ajax';
import { routes } from '../../routes/Routes';
import { MenuPage } from '../../layouts';
import { PageTitle, SettingsSummary } from '../../components';
import { actions as customerConfigurationsActions, selectors as customerConfigurationsSelectors } from '../../redux/customerConfigurations';
import { actions as customerActions, selectors as customerSelectors } from '../../redux/customers';
import { actions as countryActions, selectors as countrySelectors } from '../../redux/countries';
import { constants as sessionConstants, selectors as sessionSelectors } from '../../redux/session';
import { actions as translationsActions, selectors as translationsSelectors } from '../../redux/translations';
import { actions as tagsActions, selectors as tagsSelectors } from '../../redux/tags';

import DefaultLanguageModal from './containers/DefaulLanguageModal/DefaultLanguageModal';
import CustomerWatermarkModal from './containers/CustomerWatermarkModal/CustomerWatermarkModal';

import UsersTable from './containers/UsersTable/UsersTable';
import ReportModal from './containers/ReportModal/ReportModal';
import CustomerModal from './containers/CustomerModal/CustomerModal';
import CountryModal from './containers/CountryModal/CountryModal';
import SubscriptionsSection from './containers/SubscriptionsSection/SubscriptionsSection';
import AccountIntegrationSettingsModal from './containers/AccountIntegrationSettingsModal/AccountIntegrationSettingsModal';
import SubscriptionWarningModal from './containers/SubscriptionWarningModal/SubscriptionWarningModal';
import InactivityTimeoutModal from './containers/InactivityTimeoutModal/InactivityTimeoutModal';
import LinkExpirationModal from './containers/LinkExpirationModal/LinkExpirationModal';
import ReportLanguageModal from './containers/ReportLanguageModal/ReportLanguageModal';
import TagModal from './containers/TagModal/TagModal';
import EnabledLanguagesModal from './containers/EnabledLanguagesModal/EnabledLanguagesModal';
import MyPrivacyPolicyModal from './containers/MyPrivacyPolicyModal/MyPrivacyPolicyModal';
import MfaRequiredModal from './containers/MfaRequiredModal/MfaRequiredModal';

const loadLanguages = (dispatch) => {
  makeGet('languages').subscribe(({ response }) => {
    dispatch(translationsActions.getLanguagesSuccess(response));
  });
};

const Account = () => {
  const [showTableModal, setShowTableModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showIntegrationSettingsModal, setShowIntegrationSettingsModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [showInactivityTimeoutModal, setShowInactivityTimeoutModal] = useState(false);
  const [showCustomerWatermarkEnabledModal, setShowCustomerWatermarkEnabledModal] = useState(false);
  const [showReportLanguageModal, setShowReportLanguageModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showEnabledLanguagesModal, setShowEnabledLanguagesModal] = useState(false);
  const [showPrivacyPolicyModal, setShowPrivacyPolicyModal] = useState(false);
  const [showLinkExpirationModal, setShowLinkExpirationModal] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [showMfaRequiredModal, setShowMfaRequiredModal] = useState(false);

  const [showSubscriptionWarningModal, setShowSubscriptionWarningModal] = useState(false);
  const [isLoadingCustomersDetails, setIsLoadingCustomersDetails] = useState(false);

  const dispatch = useDispatch();
  const customerDetails = useSelector(customerSelectors.selectCustomerDetails);
  const customerConfiguration = useSelector(customerConfigurationsSelectors.selectCustomerConfiguration);
  const country = useSelector((state) => countrySelectors.selectCountry(state, customerDetails?.countryId));
  const languages = useSelector(translationsSelectors.selectLanguages);
  const customerEnabledLanguages = customerConfiguration
    ? languages.filter((lang) => customerConfiguration.enabledLanguages.includes(lang.id))
    : [];
  const customerTag = useSelector(tagsSelectors.selectCustomerTag);
  const isGlobalConfEditCustomer = useSelector(sessionSelectors.getIsGlobalConfEditCustomer);

  useEffect(() => {
    if (customerTag == null && customerDetails) {
      makeGet(`tags?customerId=${customerDetails.id}`).subscribe(({ response }) => {
        const tag = response && response.length !== 0
          ? response[0].value
          : '';
        dispatch(tagsActions.loadCustomerTagSuccess(tag));
      });
    }
  }, [customerTag, customerDetails, dispatch]);

  useEffect(() => {
    if (customerDetails) return;

    setIsLoadingCustomersDetails(true);

    makeGet('customers/details').subscribe(({ response }) => {
      setIsLoadingCustomersDetails(false);
      dispatch(customerActions.loadCustomerDetailsSuccess(response));
    }, () => {
      setIsLoadingCustomersDetails(false);
    });
  }, [customerDetails, dispatch]);

  const customerCountryId = customerDetails?.countryId;

  useEffect(() => {
    if (!customerCountryId) return;

    makeGet(`countries/${customerCountryId}`).subscribe(({ response }) => {
      dispatch(countryActions.loadCountrySuccess(response));
    });
  }, [customerCountryId, dispatch]);

  useEffect(() => {
    let isMounted = true;

    if (!customerConfiguration && isMounted) {
      makeGet('customerConfigurations/current').subscribe(({ response }) => {
        dispatch(customerConfigurationsActions.loadCurrentCustomerConfigurationSuccess(response));
      });
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (languages?.length > 0) return;

    loadLanguages(dispatch);
  }, [languages, dispatch]);

  if (languages?.length < 1) return null;

  const formatLinkExpiration = (hours) => {
    if (hours == null) return 'Off';
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return remainingHours > 0 ? `${days}d ${remainingHours}hr` : `${days}d`;
    }
    return `${hours}hr`;
  };

  const defaultLanguage = languages.find((l) => l.code === customerDetails?.defaultLanguage)?.name;
  const reportLanguage = languages.find((l) => l.code === customerConfiguration?.reportLanguage)?.name;
  const canCreateCustomers = isGlobalConfEditCustomer
    && authenticationService.currentUserValue.permissions.indexOf(sessionConstants.PERMISSION_TYPES.CREATE_CUSTOMERS) !== -1;
  const canEditMyPrivacyPolicy = authenticationService.hasPermission(sessionConstants.PERMISSION_TYPES.EDIT_MY_PRIVACY_POLICY);
  const canEditAccountIntegrationSettings = authenticationService
    .hasPermission(sessionConstants.PERMISSION_TYPES.EDIT_ACCOUNT_INTEGRATION_SETTINGS);

  if (!customerConfiguration) return null;

  return (
    <MenuPage currentRoute={routes.account}>
      <PageTitle>
        <PageTitle.Left headingText="Account" isLoading={isLoadingCustomersDetails} />

        <PageTitle.Right>
          {canCreateCustomers && (
            <button className="btn btn-primary" type="button" onClick={() => setShowCustomerModal(true)}>
              Add new customer
            </button>
          )}
        </PageTitle.Right>
      </PageTitle>

      {customerDetails && customerConfiguration && languages && (
        <div className="row">
          <div className="col-sm-6">
            <SettingsSummary
              title="Country"
              tooltipTitle="The country your organization is based in, used for regional settings and validations"
              testId="countryEditBtn"
              content={country?.name}
              onEditClick={() => setShowCountryModal(true)}
            />
            <SettingsSummary
              title="Access management"
              tooltipTitle="Manage the users who have access to your account and their roles"
              testId="accessManagementEditBtn"
              content={`${customerDetails.userCount} users`}
              onEditClick={() => setShowTableModal(true)}
            />
            {canEditAccountIntegrationSettings && (
              <SettingsSummary
                title="Integration"
                tooltipTitle="Connect third-party services such as Webhook, Insly or Scrive to your account"
                testId="integrationSettingsBtn"
                content={
                  [
                    customerConfiguration.webhookUrl && 'Webhook',
                    customerConfiguration.inslyTenantId && 'Insly',
                    customerConfiguration.scriveUserEmail && 'Scrive',
                  ].filter(Boolean).join(', ') || 'Not configured'
                }
                onEditClick={() => setShowIntegrationSettingsModal(true)}
              />
            )}
            <SettingsSummary
              title="Warning subscription usage"
              tooltipTitle="Get notified by email when your monthly subscription usage reaches this percentage"
              testId="subEditBtn"
              content={
                !customerConfiguration.subscriptionUsageWarning
                  ? 'Off'
                  : `When ${customerConfiguration.subscriptionUsageWarning}% is used`
              }
              onEditClick={() => setShowSubscriptionWarningModal(true)}
            />
            <SettingsSummary
              title="Customer watermark"
              tooltipTitle="When enabled, your custom logo is applied as a watermark on captured photos"
              testId="customerWatermarkEditBtn"
              content={customerConfiguration.customerWatermarkEnabled ? 'On' : 'Off'}
              onEditClick={() => setShowCustomerWatermarkEnabledModal(true)}
            />
            {
              customerConfiguration && customerConfiguration.photoSeriesSharingEnabled && (
                <SettingsSummary
                  title="Tag"
                  tooltipTitle="When enabled, lets you share photo series with other companies using a shared tag"
                  testId="tagBtn"
                  content={`${customerConfiguration.sharingToCompanyEnabled ? 'On' : 'Off'}${(customerTag ? (` / ${customerTag}`) : '')}`}
                  onEditClick={() => setShowTagModal(true)}
                />
              )
            }
            {canEditMyPrivacyPolicy && (
              <SettingsSummary
                title="My privacy policy"
                tooltipTitle="When enabled, users must accept your custom privacy policy before using SmartScan"
                testId="privacyPolicyBtn"
                content={`${customerConfiguration.privacyPolicySettings.enabled ? 'On' : 'Off'}`}
                onEditClick={() => setShowPrivacyPolicyModal(true)}
              />
            )}
          </div>
          <div className="col-sm-6">
            <SettingsSummary
              title="Report email"
              tooltipTitle="The email address that generated reports and notifications are sent to"
              testId="reportEmailEditBtn"
              content={customerDetails.reportingEmail}
              onEditClick={() => setShowReportModal(true)}
            />
            <SettingsSummary
              title="Enabled languages"
              tooltipTitle="Languages available for SmartScan users and generated damage reports"
              testId="enabledLanguagesBtn"
              content={`${customerConfiguration.enabledLanguages.length}`}
              onEditClick={() => setShowEnabledLanguagesModal(true)}
            />
            <SettingsSummary
              title="Report language"
              tooltipTitle="The language used when generating damage reports"
              testId="reportLanguageBtn"
              content={reportLanguage}
              onEditClick={() => setShowReportLanguageModal(true)}
            />
            <SettingsSummary
              title="Default link generation language"
              tooltipTitle="The default language used when generating new SmartScan capture links"
              testId="langEditBtn"
              content={defaultLanguage}
              onEditClick={() => setShowLanguageModal(true)}
            />
            <SettingsSummary
              title="Default link expiration"
              tooltipTitle="How long generated capture links remain valid before they expire"
              testId="linkExpirationEditBtn"
              content={formatLinkExpiration(customerConfiguration.photoCaptureLinkExpirationInHours)}
              onEditClick={() => setShowLinkExpirationModal(true)}
            />
            <SettingsSummary
              title="Inactivity timeout"
              tooltipTitle="If X number of minutes passes between taking two external images, SmartScan will restart the users session"
              testId="inactivityTimeoutEditBtn"
              content={
                !customerConfiguration.inactivityTimeout
                  ? 'No timeout'
                  : `${customerConfiguration.inactivityTimeout} minutes`
              }
              onEditClick={() => setShowInactivityTimeoutModal(true)}
            />
            <SettingsSummary
              title="Multi-factor authentication"
              tooltipTitle="When enabled, multi-factor authentication is enforced for all users of this customer"
              testId="mfaRequiredEditBtn"
              content={customerConfiguration.mfaEnabled ? 'On' : 'Off'}
              onEditClick={() => setShowMfaRequiredModal(true)}
            />
          </div>
        </div>
      )}

      {showTableModal && <UsersTable show={showTableModal} id={customerDetails.id} onHide={() => setShowTableModal(false)} />}
      {canEditMyPrivacyPolicy && (
        <MyPrivacyPolicyModal
          show={showPrivacyPolicyModal}
          privacyPolicySettings={customerConfiguration.privacyPolicySettings}
          onModalClose={() => setShowPrivacyPolicyModal(false)}
          languages={
            languages.filter((lang) => customerConfiguration.enabledLanguages.includes(lang.id))
          }
        />
      )}
      {canEditAccountIntegrationSettings && (
        <AccountIntegrationSettingsModal
          show={showIntegrationSettingsModal}
          onModalClose={() => setShowIntegrationSettingsModal(false)}
        />
      )}
      <ReportModal show={showReportModal} onModalClose={() => setShowReportModal(false)} />
      {showCountryModal && (
        <CountryModal
          show={showCountryModal}
          country={country}
          customerDetails={customerDetails}
          onModalClose={() => setShowCountryModal(false)}
        />
      )}
      <DefaultLanguageModal languages={customerEnabledLanguages} show={showLanguageModal} onModalClose={() => setShowLanguageModal(false)} />
      <LinkExpirationModal
        show={showLinkExpirationModal}
        customerConfiguration={customerConfiguration}
        onModalClose={() => setShowLinkExpirationModal(false)}
      />
      <CustomerWatermarkModal show={showCustomerWatermarkEnabledModal} onModalClose={() => setShowCustomerWatermarkEnabledModal(false)} />
      {showMfaRequiredModal && (
        <MfaRequiredModal show={showMfaRequiredModal} onModalClose={() => setShowMfaRequiredModal(false)} />
      )}
      <ReportLanguageModal
        show={showReportLanguageModal}
        customerConfiguration={customerConfiguration}
        onModalClose={() => setShowReportLanguageModal(false)}
        languages={customerEnabledLanguages}
      />
      {
        customerConfiguration && customerTag != null && customerConfiguration.photoSeriesSharingEnabled && (
          <TagModal
            show={showTagModal}
            customerConfiguration={customerConfiguration}
            customerTag={customerTag}
            onModalClose={() => setShowTagModal(false)}
          />
        )
      }

      {showCustomerModal && <CustomerModal show={showCustomerModal} onModalClose={() => setShowCustomerModal(false)} />}
      {showSubscriptionWarningModal && (
      <SubscriptionWarningModal
        shownModal={showSubscriptionWarningModal}
        customerConfiguration={customerConfiguration}
        onModalClose={() => setShowSubscriptionWarningModal(false)}
      />
      )}
      {showInactivityTimeoutModal && (
        <InactivityTimeoutModal
          customerConfiguration={customerConfiguration}
          show={showInactivityTimeoutModal}
          onModalClose={() => setShowInactivityTimeoutModal(false)}
        />
      )}
      {
        showEnabledLanguagesModal && (
          <EnabledLanguagesModal
            show={showEnabledLanguagesModal}
            customerConfiguration={customerConfiguration}
            onModalClose={() => setShowEnabledLanguagesModal(false)}
            languages={languages}
          />
        )
      }
      <SubscriptionsSection />
    </MenuPage>
  );
};

export default Account;
